import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, FloppyDisk, CalendarCheck, Package } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { PRODUCT_CATALOG, WAREHOUSES, PAYMENT_STAGES, formatMoney, lineTotal } from "../lib/constants";
import { toast } from "sonner";

const CATEGORIES = Object.keys(PRODUCT_CATALOG);

export default function OrderNew() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [orderType, setOrderType] = useState("standard"); // 'standard' | 'installment'
  const [paymentStage, setPaymentStage] = useState("1st payment");

  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", address: "", payment_name: "" });
  const [items, setItems] = useState([
    { category: "Solar Panel", name: "", quantity: 1, unit_price: 0, truck_fee: 0 },
  ]);
  const [assignedAdminId, setAssignedAdminId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users", { params: { role: "admin" } });
        setAdmins(data);
        if (data.length > 0) setAssignedAdminId(data[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const total = useMemo(() => items.reduce((s, it) => s + lineTotal(it), 0), [items]);

  const updateItem = (idx, patch) =>
    setItems((curr) => curr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((curr) => [...curr, { category: "Solar Panel", name: "", quantity: 1, unit_price: 0, truck_fee: 0 }]);
  const removeItem = (idx) => setItems((curr) => curr.filter((_, i) => i !== idx));

  const toggleWarehouse = (name) => {
    setWarehouses((curr) => (curr.includes(name) ? curr.filter((w) => w !== name) : [...curr, name]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name.trim() || !customer.phone.trim() || !customer.address.trim()) {
      toast.error("Please complete customer name, phone and address.");
      return;
    }
    if (!assignedAdminId) {
      toast.error("Please pick an admin to assign this order to.");
      return;
    }
    const cleanItems = items
      .filter((i) => i.name && Number(i.quantity) > 0)
      .map((i) => ({
        category: i.category,
        name: i.name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        truck_fee: Number(i.truck_fee) || 0,
      }));
    if (cleanItems.length === 0) {
      toast.error("Add at least one product line.");
      return;
    }
    if (warehouses.length === 0) {
      toast.error("Pick at least one warehouse for pickup / delivery.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer,
        items: cleanItems,
        notes,
        assigned_admin_id: assignedAdminId,
        payment_method: paymentMethod,
        delivery_date: deliveryDate || null,
        warehouses,
        order_type: orderType,
        payment_stage: orderType === "installment" ? paymentStage : null,
      });
      toast.success(`Order ${data.order_id} submitted.`);
      navigate(`/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="new-order-page">
      <header className="mb-8">
        <div className="overline mb-2">// New Order</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Submit Sales Order</h1>
        <p className="text-[var(--muted)] mt-2">
          Capture customer details, product lines, warehouses and payment mode. Installment orders will loop between Sales → Admin → Finance until the Final payment.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-8" data-testid="new-order-form">
        {/* Order type */}
        <Section title="Order type" step="00">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-0 border border-[var(--line)]">
            <TypeOption
              active={orderType === "standard"}
              onClick={() => setOrderType("standard")}
              title="Standard"
              desc="Full payment upfront. Flows Sales → Admin → Finance → Warehouse."
              testid="order-type-standard"
            />
            <TypeOption
              active={orderType === "installment"}
              onClick={() => setOrderType("installment")}
              title="Installment"
              desc="Customer pays in parts. Order loops Sales → Admin → Finance until the Final payment."
              testid="order-type-installment"
            />
          </div>
          {orderType === "installment" && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Current payment stage *">
                <select className="select-tech" value={paymentStage}
                  onChange={(e) => setPaymentStage(e.target.value)} data-testid="payment-stage">
                  {PAYMENT_STAGES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
          )}
        </Section>

        {/* Customer */}
        <Section title="Customer details" step="01">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Customer / company name *">
              <input className="input-tech" required value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="cust-name" />
            </Field>
            <Field label="Phone *">
              <input className="input-tech" required value={customer.phone}
                onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="cust-phone" />
            </Field>
            <Field label="Email">
              <input type="email" className="input-tech" value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="cust-email" />
            </Field>
            <Field label="Name on payment">
              <input className="input-tech" placeholder="Who the payment came from"
                value={customer.payment_name}
                onChange={(e) => setCustomer({ ...customer, payment_name: e.target.value })}
                data-testid="cust-payment-name" />
            </Field>
            <Field label="Delivery address *" full>
              <textarea rows={2} className="textarea-tech" required value={customer.address}
                onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="cust-address" />
            </Field>
          </div>
        </Section>

        {/* Items */}
        <Section title="Product lines" step="02">
          <div className="space-y-3">
            {items.map((it, idx) => {
              const productOptions = PRODUCT_CATALOG[it.category] || [];
              return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start border border-[var(--line)] p-3" data-testid={`item-row-${idx}`}>
                  <div className="col-span-12 md:col-span-3">
                    <label className="label-tech">Category</label>
                    <select className="select-tech" value={it.category}
                      onChange={(e) => updateItem(idx, { category: e.target.value, name: "" })}
                      data-testid={`item-cat-${idx}`}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <label className="label-tech">Product</label>
                    <select className="select-tech" value={it.name}
                      onChange={(e) => updateItem(idx, { name: e.target.value })}
                      data-testid={`item-name-${idx}`} required>
                      <option value="">Select…</option>
                      {productOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="label-tech">Qty</label>
                    <input type="number" min={1} className="input-tech" value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      data-testid={`item-qty-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="label-tech">Unit price</label>
                    <input type="number" min={0} step="0.01" className="input-tech" value={it.unit_price}
                      onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                      data-testid={`item-price-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="label-tech">Truck fee / unit</label>
                    <input type="number" min={0} step="0.01" className="input-tech" value={it.truck_fee}
                      placeholder="Optional"
                      onChange={(e) => updateItem(idx, { truck_fee: e.target.value })}
                      data-testid={`item-truck-${idx}`} />
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end">
                    <button type="button" onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] p-2 disabled:opacity-30"
                      data-testid={`item-remove-${idx}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                  <div className="col-span-12 text-right text-xs text-[var(--muted)]">
                    Line total: <span className="font-display font-bold text-sm text-[var(--ink)]">
                      {formatMoney(lineTotal(it))}
                    </span>
                    {Number(it.truck_fee) > 0 && (
                      <span className="ml-2 text-[var(--muted)]">
                        (incl. {formatMoney(Number(it.truck_fee) * Number(it.quantity))} truck)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={addItem} className="btn-ghost inline-flex items-center gap-2" data-testid="add-item-btn">
              <Plus size={14} weight="bold" /> Add item
            </button>
          </div>
        </Section>

        {/* Assignment */}
        <Section title="Assignment, logistics & warehouses" step="03">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Assigned admin *">
              <select className="select-tech" value={assignedAdminId}
                onChange={(e) => setAssignedAdminId(e.target.value)}
                required data-testid="assigned-admin">
                <option value="">Select admin…</option>
                {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Payment method">
              <select className="select-tech" value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)} data-testid="payment-method">
                <option>Bank Transfer</option>
                <option>POS</option>
                <option>Cash</option>
                <option>Cheque</option>
                <option>Card</option>
              </select>
            </Field>
            <Field label="Preferred delivery date">
              <input type="date" className="input-tech" value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)} data-testid="delivery-date" />
            </Field>
            <Field label="Warehouses (pick one or more) *" full>
              <div className="flex flex-wrap gap-2" data-testid="warehouse-picker">
                {WAREHOUSES.map((w) => {
                  const on = warehouses.includes(w);
                  return (
                    <button
                      type="button"
                      key={w}
                      onClick={() => toggleWarehouse(w)}
                      data-testid={`wh-${w.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors border ${
                        on
                          ? "bg-[var(--solar)] text-white border-[var(--solar)]"
                          : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--ink)]"
                      }`}
                    >
                      <Package size={12} weight="bold" /> {w}
                    </button>
                  );
                })}
              </div>
              {warehouses.length === 0 && (
                <div className="text-xs text-[var(--muted)] mt-2">
                  Pick at least one warehouse. Sales can select multiple pickup locations.
                </div>
              )}
            </Field>
            <Field label="Notes for admin" full>
              <textarea rows={3} className="textarea-tech" value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything special the admin should know about this order…"
                data-testid="order-notes" />
            </Field>
          </div>
        </Section>

        <div className="flex items-center justify-between border-t border-[var(--ink)] pt-6">
          <div>
            <div className="overline">Order total {orderType === "installment" && `· ${paymentStage}`}</div>
            <div className="font-display font-black text-4xl">{formatMoney(total)}</div>
          </div>
          <button type="submit" disabled={submitting} className="btn-solar inline-flex items-center gap-2" data-testid="submit-order-btn">
            <FloppyDisk size={18} weight="bold" />
            {submitting ? "Submitting…" : "Submit order"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, step, children }) {
  return (
    <section className="grid-line bg-white">
      <div className="border-b border-[var(--line)] flex items-center justify-between px-5 py-3">
        <div className="font-display font-bold tracking-tight text-base">{title}</div>
        <div className="overline text-[var(--solar)]">// {step}</div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-3" : ""}>
      <label className="label-tech">{label}</label>
      {children}
    </div>
  );
}

function TypeOption({ active, onClick, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-5 transition-colors border-r border-[var(--line)] last:border-r-0 ${
        active ? "bg-[var(--solar)] text-white" : "bg-white hover:bg-[#F8FAFC]"
      }`}
    >
      <div className={`font-display font-bold text-lg ${active ? "text-white" : "text-[var(--ink)]"}`}>{title}</div>
      <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-[var(--muted)]"}`}>{desc}</div>
    </button>
  );
}
